import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { ShSeverity } from '../models';
import { ShTagComponent } from './tag.component';

/**
 * Status -> severity mapping for `sh-status`. Which SHIPM8 status maps to which
 * severity lives in data or configuration (a `const` in the page), never in
 * CSS -- statuses are configurable per customer. The label is a translation key.
 */
export type StatusSeverity = ShSeverity;

export type StatusSeverityMap = Record<
  string,
  { label: string; severity: StatusSeverity }
>;

/**
 * A status as a tag, coloured by a status -> severity mapping the page
 * supplies. An unknown status falls back to `secondary` and shows the raw
 * value.
 *
 *   <sh-status [status]="row.status" [mapping]="statusMapping" />
 */
@Component({
  selector: 'sh-status',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [ShTagComponent],
  template: `<sh-tag [label]="label()" [severity]="severity()"></sh-tag>`,
})
export class ShStatusComponent {
  readonly status = input<string | number | null | undefined>(null);
  readonly mapping = input.required<StatusSeverityMap>();

  private readonly _key = computed(() => {
    const value = this.status();
    return value === null || value === undefined ? undefined : String(value);
  });

  private readonly _entry = computed(() => {
    const key = this._key();
    return key === undefined ? undefined : this.mapping()[key];
  });

  protected readonly severity = computed<ShSeverity>(
    () => this._entry()?.severity ?? 'secondary'
  );
  protected readonly label = computed(
    () => this._entry()?.label ?? this._key() ?? ''
  );
}
